import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAddItemToCartMutation } from "../../api/cartApi";
import type {
  TransformedProduct,
  TransformedVariation,
} from "../../../product/types/productApiTypes";
import type { SelectedAttributeSlugs } from "../../../product/hooks/useProductVariationSelector";
import type { AddCartItemParams } from "../../types/cartApiTypes";

// --- Cart Message Type ---
interface CartMessage {
  type: "success" | "error";
  text: string;
}

// --- DetailsActions Props ---
interface DetailsActionsProps {
  product: TransformedProduct;
  selectedVariation: TransformedVariation | null;
  isVariable: boolean;
  isCombinationAvailable: boolean;
  hasRequiredAttributes: boolean;
  variationsLoading: boolean; // Combined loading state from parent
  stockStatus:
    | TransformedProduct["stockStatus"]
    | TransformedVariation["stockStatus"];
  stockQuantity: number | null | undefined;
  selectedAttributes: SelectedAttributeSlugs;
  showQuantitySelector?: boolean; // New prop
  showViewDetailsButton?: boolean; // New prop
  containerClassName?: string; // Optional class for the main container
}

// --- DetailsActions Component ---
const DetailsActions: React.FC<DetailsActionsProps> = ({
  product,
  selectedVariation,
  isVariable,
  isCombinationAvailable,
  hasRequiredAttributes,
  variationsLoading,
  stockStatus,
  stockQuantity,
  selectedAttributes,
  showQuantitySelector = true, // Default to true
  showViewDetailsButton = false, // Default to false
  containerClassName = "cart-form", // Default class
}) => {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState<number>(1);
  const [cartMessage, setCartMessage] = useState<CartMessage | null>(null);

  const [
    addItemToCart,
    { isLoading: addingToCart, isError: addToCartError, error: cartErrorData },
  ] = useAddItemToCartMutation();

  const isLoading = variationsLoading;
  const isOutOfStock = stockStatus === "outofstock";
  const productName = product.name;
  const hasStockLimit = typeof stockQuantity === "number" && stockQuantity >= 0;
  const maxQuantity = hasStockLimit ? stockQuantity : Infinity;

  // --- Event Handlers ---
  const handleQuantityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = parseInt(e.target.value, 10);
    let newQuantity = isNaN(value) || value < 1 ? 1 : value;
    if (hasStockLimit && newQuantity > maxQuantity) newQuantity = maxQuantity;
    if (newQuantity < 1 && maxQuantity >= 1) newQuantity = 1;
    else if (maxQuantity === 0) newQuantity = 0;
    setQuantity(newQuantity);
  };

  const decreaseQuantity = (): void => {
    setQuantity((prevQuantity) => Math.max(1, prevQuantity - 1));
  };

  const increaseQuantity = (): void => {
    setQuantity((prevQuantity) => Math.min(prevQuantity + 1, maxQuantity));
  };

  const handleAddToCartSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();
    setCartMessage(null);

    if (isVariable && !isCombinationAvailable && hasRequiredAttributes) {
      setCartMessage({
        type: "error",
        text: t("addToCart.errorUnavailableCombination"),
      });
      return;
    }
    if (isVariable && !hasRequiredAttributes) {
      setCartMessage({
        type: "error",
        text: t("addToCart.errorSelectOptions"),
      });
      return;
    }
    if (isOutOfStock) {
      setCartMessage({ type: "error", text: t("addToCart.errorOutOfStock") });
      return;
    }

    const itemToAdd: AddCartItemParams = {
      id: isVariable && selectedVariation ? selectedVariation.id : product.id,
      quantity: quantity,
    };

    if (isVariable && selectedVariation) {
      itemToAdd.variation = Object.entries(selectedAttributes).map(
        ([attribute, value]) => ({
          attribute: attribute,
          value: value,
        })
      );
    }

    try {
      await addItemToCart(itemToAdd).unwrap();
      setCartMessage({
        type: "success",
        text: t("addToCart.successMessage"),
      });
      if (showQuantitySelector) setQuantity(1); // Reset quantity only if selector is shown
    } catch (err: any) {
      console.error("Failed to add item to cart from card:", err);
      let errorMessage = t("addToCart.errorGeneral"); // Default general error

      // Check for specific error code
      if (
        err?.data?.code === "woocommerce_rest_product_partially_out_of_stock"
      ) {
        // Use the specific message translation
        errorMessage = t("addToCart.errorPartiallyOutOfStock");
      }
      setCartMessage({
        type: "error",
        text: errorMessage,
      });
    }
  };

  // --- Effects ---
  useEffect(() => {
    setCartMessage(null);
  }, [product.id, selectedVariation?.id]);

  useEffect(() => {
    if (cartMessage) {
      const timer = setTimeout(() => setCartMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [cartMessage]);

  useEffect(() => {
    if (hasStockLimit && quantity > maxQuantity) {
      setQuantity(maxQuantity > 0 ? maxQuantity : 1);
    }
  }, [stockQuantity, hasStockLimit, maxQuantity, quantity]);

  // --- Render ---
  return (
    <form className={containerClassName} onSubmit={handleAddToCartSubmit}>
      <div className="details-actions-row">
        {/* --- Conditionally render Quantity Selector --- */}
        {showQuantitySelector && (
          <div className="quantity">
            <label htmlFor={`quantity-${product.id}`} className="sr-only">
              {t("product.quantity")}
            </label>{" "}
            {/* Added sr-only label */}
            <div className="quantity-input">
              <button
                type="button"
                className="quantity-decrease"
                onClick={decreaseQuantity}
                disabled={
                  quantity <= 1 ||
                  isOutOfStock ||
                  isLoading ||
                  addingToCart ||
                  (isVariable &&
                    !isCombinationAvailable &&
                    hasRequiredAttributes)
                }
                aria-label={t("addToCart.decreaseQuantity")}
              >
                -
              </button>
              <input
                type="number"
                id={`quantity-${product.id}`} // Unique ID
                name="quantity"
                value={quantity}
                min="1"
                max={hasStockLimit ? maxQuantity : undefined}
                onChange={handleQuantityChange}
                disabled={
                  isOutOfStock ||
                  isLoading ||
                  addingToCart ||
                  (isVariable &&
                    !isCombinationAvailable &&
                    hasRequiredAttributes) ||
                  maxQuantity === 0
                }
                aria-label={t("product.quantity")}
              />
              <button
                type="button"
                className="quantity-increase"
                onClick={increaseQuantity}
                disabled={
                  quantity >= maxQuantity ||
                  isOutOfStock ||
                  isLoading ||
                  addingToCart ||
                  (isVariable &&
                    !isCombinationAvailable &&
                    hasRequiredAttributes)
                }
                aria-label={t("addToCart.increaseQuantity")}
              >
                +
              </button>
            </div>
          </div>
        )}

        {/* --- Conditionally render View Details Button (if enabled) --- */}
        {showViewDetailsButton && (
          <a href={product.permalink} className="view-details-button">
            {t("productModal.viewDetails")}
          </a>
        )}

        {/* --- Add to Cart Button --- */}
        <button
          type="submit"
          className="add-to-cart-button"
          disabled={
            isOutOfStock ||
            addingToCart ||
            isLoading ||
            (isVariable && !isCombinationAvailable && hasRequiredAttributes) ||
            quantity <= 0
          }
        >
          {addingToCart
            ? t("addToCart.adding")
            : isOutOfStock
            ? t("product.outOfStock")
            : isLoading
            ? t("product.loading")
            : isVariable && !isCombinationAvailable && hasRequiredAttributes
            ? t("product.unavailable")
            : t("product.addToCart")}
        </button>
      </div>{" "}
      {/* End of details-actions-row */}
      {/* Display Cart Messages */}
      {cartMessage && (
        <div className={`cart-message ${cartMessage.type}`}>
          {cartMessage.text}
        </div>
      )}
    </form>
  );
};

export default DetailsActions;
